'use client'

import React, { useEffect } from 'react'
import type { Header, Identity } from '@/payload-types'
import { MobileMenuNav } from './MobileMenuNav'
import { PillCap } from '@/components/ui/PillCap'
import './mobileMenu.scss'

interface MobileMenuProps {
  data: Header
  identity: Identity
  isOpen: boolean
  onClose: () => void
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ data, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className={`mobile-menu ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-menu-header">
          <p className="fw-500 m-0 text-white fs-4">Menu</p>
          <button className="mobile-menu-close" onClick={onClose} aria-label="Close menu">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="mobile-menu-body">
          {/* CTA Buttons */}
          <div className="mobile-menu-cta">
            {data.ctaButtonSuccess?.text && data.ctaButtonSuccess?.url && (
              <a
                className="btn btn-pill btn-pill-glow w-100"
                href={data.ctaButtonSuccess.url}
              >
                {data.ctaButtonSuccess.text}
                <PillCap />
              </a>
            )}

            {data.ctaButtonDanger?.text && data.ctaButtonDanger?.url && (
              <a
                className="btn btn-pill btn-pill-outline w-100"
                href={data.ctaButtonDanger.url}
              >
                {data.ctaButtonDanger.text}
                <PillCap />
              </a>
            )}

            {data.ctaButtonPrimary?.text && data.ctaButtonPrimary?.url && (
              <a
                className="btn btn-pill btn-pill-primary w-100"
                href={data.ctaButtonPrimary.url}
              >
                {data.ctaButtonPrimary.text}
                <PillCap />
              </a>
            )}
          </div>

          {/* Navigation Accordion */}
          <MobileMenuNav data={data} />
        </div>
      </div>
    </div>
  )
}
